import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Menu, MenuItem,
  BottomNavigation, BottomNavigationAction, Paper, Divider,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Pets as PetsIcon,
  CalendarToday as CalendarIcon,
  Receipt as InvoiceIcon,
  MedicalServices as MedicalIcon,
  Home as HomeIcon,
  Medication as RxIcon,
} from '@mui/icons-material';
import { useClientPortalStore } from '../stores/clientPortalStore';

interface ClientPortalLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { label: 'Home', icon: <HomeIcon />, path: '/client-portal' },
  { label: 'My Pets', icon: <PetsIcon />, path: '/client-portal/pets' },
  { label: 'Appointments', icon: <CalendarIcon />, path: '/client-portal/appointments' },
  { label: 'Invoices', icon: <InvoiceIcon />, path: '/client-portal/invoices' },
  { label: 'Medical', icon: <MedicalIcon />, path: '/client-portal/medical' },
  { label: 'Refills', icon: <RxIcon />, path: '/client-portal/refills' },
];

const ClientPortalLayout = ({ children }: ClientPortalLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { client, logout } = useClientPortalStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/client-portal/login');
  };

  const currentNavValue = navItems.findIndex(item => location.pathname === item.path);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed">
        <Toolbar>
          <PetsIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Pet Portal
          </Typography>
          <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
            {client?.full_name}
          </Typography>
          <IconButton color="inherit" onClick={e => setAnchorEl(e.currentTarget)}>
            <AccountIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>
              <Typography variant="body2">{client?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }}>Sign Out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, p: 2, mt: 8, mb: 8 }}>
        {children}
      </Box>

      {/* Bottom navigation (mobile-first) */}
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <BottomNavigation
          value={currentNavValue >= 0 ? currentNavValue : false}
          onChange={(_, newValue) => navigate(navItems[newValue].path)}
        >
          {navItems.map(item => (
            <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default ClientPortalLayout;
